/**
 * @file apiEndPoints.ts
 * @module api/apiEndPoints
 * @description API endpoint URL constants and configuration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

// API END Points
import * as constants from "../utils/constants";
import { warehouse } from "../utils/storeString";

/**---------------Authentication------------------------------ */
export const LOGIN = "admin/login" + " " + constants.POST_RAW;
export const LOGOUT = "admin/logout" + " " + constants.POST_RAW;
export const PEOPLE_CHAT = {
  list: 'chat ' + constants.GET,
  preferences: 'chat/preferences ' + constants.GET,
  savePreferences: 'chat/preferences ' + constants.POST_RAW,
  create: 'chat/conversations ' + constants.POST_RAW,
  history: 'chat/messages ' + constants.GET_URL_PARAMS,
  send: 'chat/messages ' + constants.POST_FORM,
  read: 'chat/read ' + constants.POST_RAW,
  download: 'chat/attachment ' + constants.GET_URL_PARAMS,
};
export const SIDEBAR = "admin/dashboard/stats" + " " + constants.GET;
export const DASHBOARD = "admin/dashboard" + " " + constants.GET_URL_PARAMS;
export const UPROCURE_DASHBOARD = "admin/uprocure-dashboard" + " " + constants.GET_URL_PARAMS;
export const UPROCURE_DASHBOARD_ORDER_REPORT = "_product/manage/shipments/vendors/shipment-report" + " " + constants.GET_URL_PARAMS;
export const ASSIST_ADMIN_DASHBOARD = "assist-admin/dashboard/stats" + " " + constants.GET_URL_PARAMS;

export const AUTH = {
  RESEND: "admin/otp/resend" + " " + constants.PATCH,
  VERIFY: "admin/otp/verify" + " " + constants.POST_URL_ENCODED,
  FORGOTPASSWORD: "admin/password/forgot" + " " + constants.POST_URL_ENCODED,
  VERIFYPASSWORD: "admin/password/code/verify" + " " + constants.POST_URL_ENCODED,
  RESETPASSWORD: "admin/password/reset" + " " + constants.POST_URL_ENCODED,
  DASHBOARD: "admin/dashboard" + " " + constants.GET,
  checkPassword: "admin/check-password" + " " + constants.POST_RAW,
  notification: "admin/notifications" + " " + constants.GET_URL_PARAMS,
  readNotifications: "admin/notifications" + " " + constants.PATCH,
  notificationCount: "admin/notifications-count" + " " + constants.GET,
};
export const master = {
  // Primary category
  addCategory: "business/categories" + " " + constants.POST_FORM,
  editCategory: "business/categories" + " " + constants.MULTI_PART_ID_PATCH,
  deleteCategory: "business/categories" + " " + constants.DELETE_ID_PARAMS,
  categoryList: "business/categories" + " " + constants.GET_URL_PARAMS,
  // Sub category
  addSubCategory: "business/categories/sub" + " " + constants.POST_FORM,
  editSubCategory:
    "business/categories/sub" + " " + constants.MULTI_PART_ID_PATCH,
  deleteSubCategory:
    "business/categories/sub" + " " + constants.MULTI_PART_ID_DELETE,
  // Group category
  addGroupCategory: "business/categories/group" + " " + constants.POST_FORM,
  editGroupCategory:
    "business/categories/group" + " " + constants.MULTI_PART_ID_PATCH,
  deleteGroupCategory:
    "business/categories/group" + " " + constants.MULTI_PART_ID_DELETE,
  // Business ceritificates
  addCertificate: "business/config/certificate" + " " + constants.POST_FORM,
  listCertificate:
    "business/config/certificate" + " " + constants.GET_URL_PARAMS,
  editCertificate:
    "business/config/certificate" + " " + constants.MULTI_PART_ID_PATCH,
  deleteCertificate:
    "business/config/certificate" + " " + constants.DELETE_ID_PARAMS,
  // Business types
  addBusinessTypes: "business/types" + " " + constants.POST_FORM,
  editBusinessTypes: "business/types" + " " + constants.MULTI_PART_ID_PATCH,
  deleteBusinessTypes: "business/types" + " " + constants.DELETE_ID_PARAMS,
  listBusinessTypes: "business/types" + " " + constants.GET_URL_PARAMS,
  // Brands
  addBrands: "_product/default/brands" + " " + constants.POST_FORM,
  editBrands: "_product/default/brands" + " " + constants.MULTI_PART_ID_PATCH,
  deleteBrands: "_product/default/brands" + " " + constants.DELETE_ID_PARAMS,
  listBrands: "_product/default/brands" + " " + constants.GET_URL_PARAMS,
  //Technical Details
  technicalCategories:
    "_product/default/technical-detail-fields/categories" + " " + constants.GET,
  technicalDetailFeild:
    "_product/default/technical-detail-fields/categories" +
    " " +
    constants.GET_ID_PARAMS,
  addTechnicalDetails:
    "_product/default/technical-detail-fields" + " " + constants.POST_RAW,
  editTechnicalDetails:
    "_product/default/technical-detail-fields" + " " + constants.PATCH_ID,
  deleteTechnicalDetails:
    "_product/default/technical-detail-fields" +
    " " +
    constants.DELETE_ID_PARAMS,
  //Product Variants
  variantTiles: "_product/default/variants" + " " + constants.GET_URL_PARAMS,
  addVariants: "_product/default/variants" + " " + constants.POST_RAW,
  editVariants: "_product/default/variants" + " " + constants.PATCH_ID,
  deleteVariants:
    "_product/default/variants" + " " + constants.DELETE_ID_PARAMS,
  deleteSubVariants:
    "_product/default/variants" + " " + constants.MULTI_PART_ID_DELETE,
  variantInfo: "_product/default/variants" + " " + constants.GET_URL_ID_PARAMS,
  addArea: "business/loading-areas" + " " + constants.POST_FORM,
  listArea: "business/loading-areas" + " " + constants.GET_URL_PARAMS,
  editArea:
    "business/loading-areas" + " " + constants.PATCH_FORM_ID_URL_ENCODED,
  info: "business/loading-areas" + " " + constants.GET_ID_PARAMS,
  updateAreaStatus: "business/loading-areas/status" + " " + constants.PATCH_ID,
  assignLoadingArea: "business/assign-loading-areas" + " " + constants.POST_RAW,
  // roles and permission
  addRoles: "config/role" + " " + constants.POST_RAW,
  getRoles: "config/role" + " " + constants.GET_URL_PARAMS,
  getRoleDetails: "config/role" + " " + constants.GET_ID_PARAMS,
  editDetails: "config/role" + " " + constants.PATCH_ID,
  deleteRole: "config/role" + " " + constants.DELETE_ID_PARAMS,
  // user management
  addUser: "user-management" + " " + constants.POST_FORM,
  getUsers: "user-management" + " " + constants.GET_URL_PARAMS,
  updateUserStatus: "user-management/status" + " " + constants.PATCH_ID,
  deleteUser: "user-management" + " " + constants.DELETE_ID_PARAMS,
  getUserDetails: "user-management" + " " + constants.GET_ID_PARAMS,
  editUserDetails: "user-management" + " " + constants.PATCH_FORM_ID,

  // city master
  addCity: "_default/city" + " " + constants.POST_RAW,
  listCity: "_default/list-cities" + " " + constants.GET_URL_PARAMS,
  editCity: "_default/city" + " " + constants.PATCH_ID,
  listCityAndDistrict:
    "_default/city-with-districts" + " " + constants.GET_URL_PARAMS,
  // district master
  addDistrict: "_default/districts" + " " + constants.POST_RAW,
  listDistricts: "_default/districts" + " " + constants.GET_URL_PARAMS,
  editDistrict: "_default/districts" + " " + constants.PATCH_ID,
  // ward master
  listWards: "_default/wards" + " " + constants.GET_URL_PARAMS,
  addWard: "_default/wards" + " " + constants.POST_RAW,
  editWard: "_default/wards" + " " + constants.PATCH_ID,
};
export const seller = {
  kycDetails: "business/list" + " " + constants.GET_URL_PARAMS,
  checkCategories:
    "business/check-categories" + " " + constants.GET_URL_ID_PARAMS,
  sellerProfile: "business/profile" + " " + constants.GET_ID_PARAMS,
  kycStatusUpdate:
    "business/kyc/status" + " " + constants.PATCH_FORM_ID_URL_ENCODED,
  sellerProfileUpdate: "business/profile" + " " + constants.MULTI_PART_ID_PATCH,
  profileUpdateLogs:
    "business/update-logs/profile" + " " + constants.GET_ID_PARAMS,
  listSeller: "seller/manage-business/list" + " " + constants.GET_URL_PARAMS,
  updateSellerState:
    "seller/manage-business/account-state" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  addSeller: "seller/manage-business/add" + " " + constants.POST_FORM,
  listSellerProduct:
    "_product/manage/business-products/list" +
    " " +
    constants.GET_URL_ID_PARAMS,
  listSellableProduct:
    "_product/manage/business-products/sellable" +
    " " +
    constants.GET_URL_PARAMS,
  listPriceCatalogProduct:
    "_product/manage/business-products/price-catalog-product" +
    " " +
    constants.GET_URL_PARAMS,
  sellableProductInfo:
    "_product/manage/business-products/info" + " " + constants.GET_ID_PARAMS,
  listInventory:
    "_product/manage/inventory/business/v2/list" +
    " " +
    constants.GET_URL_ID_PARAMS,
  listInventoryHistory:
    "_product/manage/inventory/business/transactions" +
    " " +
    constants.GET_URL_ID_PARAMS,
  listStockDetails:
    "_product/manage/inventory/business/in-stock" +
    " " +
    constants.GET_URL_ID_PARAMS,
  listOrder: "_product/manage/orders/list" + " " + constants.GET_URL_ID_PARAMS,
  listAdvertisement:
    "_product/manage/advertisements" + " " + constants.GET_URL_ID_PARAMS,
  listGoodsRequest:
    "_product/manage/shipments/list" + " " + constants.GET_URL_ID_PARAMS,
  payoutProfiles: "business/payout-profiles" + " " + constants.GET_URL_PARAMS,
  payoutOrderList:
    "_product/manage/orders/payouts/business-orders" +
    " " +
    constants.GET_URL_ID_PARAMS,
  payoutInfo: "_product/manage/orders/payouts" + " " + constants.GET_ID_PARAMS,
  payoutList:
    "_product/manage/orders/payouts/business" +
    " " +
    constants.GET_URL_ID_PARAMS,
  payout:
    "_product/manage/orders/payouts/business" + " " + constants.POST_ID_PARAMS,
  orderSales:
    "_product/manage/orders/sales-report" + " " + constants.GET_URL_PARAMS,
  orderSalesReport:
    "_product/reports/order-sales" + " " + constants.GET_URL_PARAMS,
  orderSalesReportV2:
    "_product/reports/order-sales-v2" + " " + constants.GET_URL_PARAMS,
  orderSalesReportV3:
    "_product/reports/order-sales-v3" + " " + constants.GET_URL_PARAMS,
  orderSalesReportV4:
    "_product/reports/order-sales-v4" + " " + constants.GET_URL_PARAMS,
  orderSalesReportV5:
    "_product/reports/order-sales-v5" + " " + constants.GET_URL_PARAMS,
  orderSalesReportV6:
    "_product/reports/order-sales-v6" + " " + constants.GET_URL_PARAMS,
  orderSalesReportV7:
    "_product/reports/order-sales-v7" + " " + constants.GET_URL_PARAMS,
  orderSalesReportV8:
    "_product/reports/order-sales-v8" + " " + constants.GET_URL_PARAMS,
  returnProductReport:
    "_product/reports/return-product" + " " + constants.GET_URL_PARAMS,
  orderDelivered:
    "_product/manage/orders/list-delivered" + " " + constants.GET_URL_PARAMS,
  financeBankList: "admin/finance-bank" + " " + constants.GET_URL_PARAMS,
  financeSalesReport:
    "_product/manage/orders/finance-stats" + " " + constants.GET_URL_PARAMS,
  updateFinanceDetails:
    "_product/manage/orders/finance-details" + " " + constants.PATCH_ID,
  routeProductReport:
    "_product/reports/route-product" + " " + constants.GET_URL_PARAMS,
  salesReport:
    "_product/reports/finance-sales" + " " + constants.GET_URL_PARAMS,
  orderReport:
    "_product/reports/finance-order" + " " + constants.GET_URL_PARAMS,
  deliveryReport:
    "_product/reports/finance-delivery" + " " + constants.GET_URL_PARAMS,
  returnReport:
    "_product/reports/finance-return" + " " + constants.GET_URL_PARAMS,
};

export const activityLogsEndPoints = {
  list: "_auditLog/admin/logs" + " " + constants.GET_URL_PARAMS,
  filters: "_auditLog/admin/filters" + " " + constants.GET_URL_PARAMS,
};

export const outStandingReport = {
  list: "_product/manage/outstanding-report" + " " + constants.GET_URL_PARAMS,
  listDrivers:
    "_product/manage/outstanding-report/on-delivery-drivers" +
    " " +
    constants.GET_URL_PARAMS,
  analytics:
    "_product/manage/outstanding-report/analytics" +
    " " +
    constants.GET_URL_PARAMS,
  completePayment:
    "_product/manage/outstanding-report/complete-payments" +
    " " +
    constants.POST_RAW,
  paymentReceipt:
    "_product/manage/outstanding-report/payment-receipt" +
    " " +
    constants.PATCH_ID,

  paymentReceiptStatus:
    "_product/manage/outstanding-report/payment-receipt-status" +
    " " +
    constants.PATCH_ID,
  paymentSkipped:
    "_product/manage/outstanding-report/payment-skipped" +
    " " +
    constants.PATCH_ID,
  totalPayments:
    "_product/manage/outstanding-report/total-payments" +
    " " +
    constants.GET_URL_PARAMS,
  tigoPesaVerification:
    "_product/manage/outstanding-report/tigo-verify" +
    " " +
    constants.MULTI_PART_ID_PATCH,
};
export const product = {
  initProduct:
    "_product/default/product-templates/init" + " " + constants.GET_URL_PARAMS,
  listProduct:
    "_product/default/product-templates" + " " + constants.GET_URL_PARAMS,
  draftCount:
    "_product/default/product-templates/draft/count" + " " + constants.GET,
  draftList:
    "_product/default/product-templates/draft" + " " + constants.GET_URL_PARAMS,
  deleteProduct:
    "_product/default/product-templates" + " " + constants.DELETE_ID_PARAMS,
  updateProductState:
    "_product/default/product-templates/state/variant" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  productInfo:
    "_product/default/product-templates/variant" +
    " " +
    constants.GET_ID_PARAMS,
  addProduct: "_product/default/product-templates" + " " + constants.POST_FORM,
  initProductVariant:
    "_product/default/product-templates/init/variant" +
    " " +
    constants.GET_URL_ID_PARAMS,
  editProduct:
    "_product/default/product-templates" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  editProductVariant:
    "_product/default/product-templates/variant" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  editProductVariantPrice:
    "_product/manage-products/variants-edit-sync" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  deleteProductVariant:
    "_product/default/product-templates" + " " + constants.MULTI_PART_ID_DELETE,
  updateSearchTags:
    "_product/default/product-templates/search-tag" + " " + constants.PATCH_ID,
  setOrderLimit:
    "_product/manage-products/set-variant-order-limit" +
    " " +
    constants.PATCH_ID,
  availableToSell:
    "_product/manage-products/available-to-sell" + " " + constants.PATCH_ID,
  isPharmacyProduct:
    "_product/manage-products/pharmacy-product" + " " + constants.PATCH_ID,
};

export const manageProduct = {
  listProduct:
    "_product/manage/business-products/list" + " " + constants.GET_URL_PARAMS,
  listProductForAssign: "_product/list" + " " + constants.GET_URL_PARAMS,
  updateVariantStatus:
    "_product/manage/business-products/status/variant" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  listVariant:
    "_product/manage/business-products/list-variants" +
    " " +
    constants.GET_URL_PARAMS,
  updateSearchTags:
    "_product/manage/business-products/search-tag" + " " + constants.PATCH_ID,
  getPriceCatalogProductList:
    "_product/manage/business-products/price-catalog-product" +
    " " +
    constants.GET_URL_PARAMS,
  getPriceCatalogEntry:
    "_product/manage/business-products/price-catalog" +
    " " +
    constants.GET_ID_PARAMS,
  getPriceCatalogEntryForAuditLogFilter:
    "_product/manage/business-products/price-catalog/audit-log-filter-details" +
    " " +
    constants.GET_ID_PARAMS,
  getCriticalAlertsFilterDetails:
    "_product/manage/business-products/price-catalog/critical-alerts-filter-details" +
    " " +
    constants.GET_ID_PARAMS,
  getPriceCatalogEntries:
    "_product/manage/business-products/price-catalog/list" +
    " " +
    constants.GET_URL_PARAMS,
  getPriceCatalogGrossProfit:
    "_product/manage/business-products/price-catalog/gross-profit" +
    " " +
    constants.GET_URL_PARAMS,
  savePriceCatalogEntry:
    "_product/manage/business-products/price-catalog" +
    " " +
    constants.POST_RAW,
  // same endpoint, flagged so the backend attributes the audit log to "System" instead of the current user
  savePriceCatalogEntrySystem:
    "_product/manage/business-products/price-catalog?isSystem=true" +
    " " +
    constants.POST_RAW,
  shipmentTracking:
    "_product/shipment-tracking" + " " + constants.GET_URL_PARAMS,
  assignProductToVendor:
    "_product/manage/assign-product-to-vendor" + " " + constants.POST_RAW,
  unassignProductToVendor:
    "_product/manage/unassign-vendor-from-product" + " " + constants.POST_RAW,
  reconcileExcel:
    "_product/manage/business-products/reconcile-excel" +
    " " +
    constants.MULTI_PART_POST,
  bulkStockExcel:
    "_product/manage/business-products/bulk-stock-upload" +
    " " +
    constants.MULTI_PART_POST,
  downloadPriceImportSample:
    "static_files/price_import_sample.xlsx" + " " + constants.GET,
};

export const pendingCertificates = {
  listCertificates:
    "seller/manage-business/pending/list" + " " + constants.GET_URL_PARAMS,
  notifyCertificates:
    "seller/manage-business/pending/notify" + " " + constants.POST_ID_PARAMS,
};
export const contactInquires = {
  listInquires: "business/feedback" + " " + constants.GET_URL_PARAMS,
  listBuyerInquires:
    "_buyer/manage-buyers/feedback" + " " + constants.GET_URL_PARAMS,
};
export const buyer = {
  listBuyer: "_buyer/manage-buyers/list" + " " + constants.GET_URL_PARAMS,
  blockBuyer: "_buyer/manage-buyers/block-status" + " " + constants.PATCH_ID,
  details: "_buyer/manage-buyers/info" + " " + constants.GET_ID_PARAMS,
  updateProfile: "_buyer/manage-buyers/profile" + " " + constants.PATCH_ID,
  addBuyer: "_buyer/manage-buyers/add" + " " + constants.POST_FORM,
  addAddress:
    "_buyer/manage-buyers/delivery-addresses" + " " + constants.POST_ID_PARAMS,
  updateAddress:
    "_buyer/manage-buyers/delivery-addresses" + " " + constants.PATCH_ID,
  deleteAddress:
    "_buyer/manage-buyers/delivery-addresses" +
    " " +
    constants.MULTI_PART_ID_DELETE,
  listOnBoardedBuyers:
    "_buyer/manage-buyers/list-buyers" + " " + constants.GET_URL_PARAMS,
  assignTeamMember:
    "_buyer/manage-buyers/assign-member" + " " + constants.POST_URL_ENCODED,
  refundOrderList:
    "_buyer/manage-buyers/refunds/list" + " " + constants.GET_URL_PARAMS,
  refundBuyerOrderList:
    "_product/manage/orders/refunds/list" + " " + constants.GET_URL_PARAMS,
  refundOrderCount:
    "_product/manage/orders/refunds/orders/count" + " " + constants.GET,
  updateRefundStatus:
    "_product/manage/orders/refunds/status" + " " + constants.PATCH,
  receivablePayouts:
    "_product/manage/orders/receivable-report" + " " + constants.GET_URL_PARAMS,
  totalPayments:
    "_product/manage/orders/total-payments" + " " + constants.GET_URL_PARAMS,
  completePayouts:
    "_product/manage/orders/complete-payments" + " " + constants.POST_RAW,
  listOrder:
    "_product/manage/orders/buyer-list" + " " + constants.GET_URL_ID_PARAMS,
  dashboard:
    "_product/manage/orders/buyer-orders-report" +
    " " +
    constants.GET_URL_ID_PARAMS,
  cart: "_product/manage/buyers/carts" + " " + constants.GET_URL_ID_PARAMS,
  sendNotification:
    "_buyer/manage-buyers/send-notification" + " " + constants.POST_FORM,
  activeCustomerList:
    "_buyer/manage-buyers/active-customer-list" +
    " " +
    constants.GET_URL_PARAMS,
};
export const platformFees = {
  updateCommissionFees:
    "business/config/platform/commission-fees" + " " + constants.PATCH,
  listOtherFees: "business/config/platform/other-fees" + " " + constants.GET,
  updateOtherFees:
    "business/config/platform/other-fees" + " " + constants.PATCH,
};
export const advertisements = {
  addAddvertisement:
    "_product/manage/advertisements" + " " + constants.MULTI_PART_ID_POST,
  listAdvertisement:
    "_product/manage/advertisements/list" + " " + constants.GET_URL_PARAMS,
  payment:
    "_product/manage/advertisements/payment" + " " + constants.POST_ID_PARAMS,
  details:
    "_product/manage/advertisements/info" + " " + constants.GET_ID_PARAMS,
  deleteAdvertisement:
    "_product/manage/advertisements" + " " + constants.DELETE_ID_PARAMS,
};
export const driver = {
  addDriver: "_driver/manage-drivers/add" + " " + constants.POST_FORM,
  listDriver: "_driver/manage-drivers/list" + " " + constants.GET_URL_PARAMS,
  listDriverWithVehicle:
    "_driver/manage-drivers/driverwith-vehicle" +
    " " +
    constants.GET_URL_PARAMS,
  info: "_driver/manage-drivers/info" + " " + constants.GET_ID_PARAMS,
  updateDriver:
    "_driver/manage-drivers/profile" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  deletDriver:
    "_driver/manage-drivers/profile" + " " + constants.DELETE_ID_PARAMS,
  updateStatus: "_driver/manage-drivers/status" + " " + constants.PATCH_ID,
  listActiveDriver:
    "_driver/manage-drivers/list-active" + " " + constants.GET_URL_PARAMS,
  listDistricts: "_default/districts" + " " + constants.GET_URL_PARAMS,
  listWards: "_default/wards" + " " + constants.GET_URL_PARAMS,
};
export const vehicle = {
  addVehicle: "_driver/manage-vehicle/add" + " " + constants.POST_FORM,
  listVehicle: "_driver/manage-vehicle/list" + " " + constants.GET_URL_PARAMS,
  info: "_driver/manage-vehicle/info" + " " + constants.GET_ID_PARAMS,
  updateVehicle:
    "_driver/manage-vehicle" + " " + constants.PATCH_FORM_ID_URL_ENCODED,
  updateStatus: "_driver/manage-vehicle/status" + " " + constants.PATCH_ID,
  updateDriver:
    "_driver/manage-vehicle/change-driver" + " " + constants.PATCH_ID,
  checkAssignedDriver:
    "_driver/manage-vehicle/check-assigned" + " " + constants.GET_ID_PARAMS,
  deleteVehicle: "_driver/manage-vehicle" + " " + constants.DELETE_ID_PARAMS,
};
export const customerService = {
  add: "assist-admin" + " " + constants.POST_FORM,
  delete: "assist-admin" + " " + constants.DELETE_ID_PARAMS,
  list: "assist-admin" + " " + constants.GET_URL_PARAMS,
  status: "assist-admin/status" + " " + constants.PATCH_ID,
  edit: "assist-admin" + " " + constants.PATCH_FORM_ID_URL_ENCODED,
  monthlyTarget: "assist-admin/monthly-target" + " " + constants.POST_ID_PARAMS,
  checkAssignedManager:
    "assist-admin/check-assigned" + " " + constants.GET_ID_PARAMS,
  removeAssignedManager:
    "assist-admin/remove-assigned" + " " + constants.PATCH_ID,
  assignDistrict:
    "assist-admin/assign-district" + " " + constants.POST_ID_PARAMS,
  myTeam: "assist-admin/my-team" + " " + constants.GET_URL_ID_PARAMS,
  listDistrict: "_default/districts" + " " + constants.GET_URL_PARAMS,
  listArea:
    "_product/manage/orders/list-wards" + " " + constants.GET_URL_ID_PARAMS,
  listAssistArea:
    "_product/manage/orders/list-assist-wards" +
    " " +
    constants.GET_URL_ID_PARAMS,
  memberDetails:
    "assist-admin/member-details" + " " + constants.GET_URL_ID_PARAMS,
  districtDetails:
    "assist-admin/district-details" + " " + constants.GET_URL_ID_PARAMS,
  listDistrictManager:
    "_default/list-districts" + " " + constants.GET_URL_PARAMS,
};
export const inventory = {
  list: "_product/manage/shipments/list" + " " + constants.GET_URL_PARAMS,
  addCostInfo:
    "_product/manage/shipments/cost" + " " + constants.POST_ID_PARAMS,
  sendShipmentRequest:
    "_product/manage/shipments" + " " + constants.POST_ID_PARAMS,
  info: "_product/manage/shipments" + " " + constants.GET_ID_PARAMS,
  infoInventory:
    "_product/manage/shipments/completion-init" + " " + constants.GET_ID_PARAMS,
  updateStatus: "_product/manage/shipments/status" + " " + constants.PATCH_ID,
  listInventoryProduct:
    "_product/manage/inventory/v2/list" + " " + constants.GET_URL_PARAMS,
  inventoryStats:
    "_product/manage/inventory/stats" + " " + constants.GET_URL_PARAMS,
  listInventoryVarianTransaction:
    "_product/manage/inventory/transactions" +
    " " +
    constants.GET_URL_ID_PARAMS,
  variantStockList:
    "_product/manage/inventory/in-stock" + " " + constants.GET_ID_PARAMS,
  variantStockListBulk:
    "_product/manage/inventory/in-stock/bulk" + " " + constants.POST_RAW,
  variantUpdateInit:
    "_product/manage/inventory/update-init" + " " + constants.GET_ID_PARAMS,
  variantUpdate: "_product/manage/inventory/update" + " " + constants.PATCH_ID,
  variantExpiredStockInfo:
    "_product/manage/inventory/expiring-stock/info" +
    " " +
    constants.GET_URL_PARAMS,
  variantExpiredStockList:
    "_product/manage/inventory/expiring-stock/list" +
    " " +
    constants.GET_URL_PARAMS,
  variantRemainder:
    "_product/manage/inventory/reminder" +
    " " +
    constants.POST_URL_ENCODED_ID_PARAMS,
  initReceivedDirectGoods:
    "_product/manage/inventory/batches" + " " + constants.POST_RAW,
  receivedDirectGoods:
    "_product/manage/inventory/update-multiple" + " " + constants.PATCH_ID,
  addProductToLowStockList:
    "_product/manage/low-stock" + " " + constants.POST_RAW,
  lowStockList: "_product/manage/low-stock" + " " + constants.GET_URL_PARAMS,
  markAsOrdered: "_product/manage/low-stock" + " " + constants.PATCH_ID,
  lowStockReport:
    "_product/manage/low-stock/report" + " " + constants.GET_URL_PARAMS,
  outofstock:
    "_product/manage/inventory/out-of-stock" + " " + constants.GET_URL_PARAMS,
  inventorySnapshot:
    "_product/manage/inventory/snapshot" + " " + constants.GET_URL_PARAMS,
};
export const ordersDelivery = {
  init: "_product/manage/orders/init" + " " + constants.GET_URL_PARAMS,
  add: "_product/manage/orders" + " " + constants.POST_RAW,
  edit: "_product/manage/orders" + " " + constants.PATCH_ID,
  info: "_product/manage/orders/info" + " " + constants.GET_ID_PARAMS,
  list: "_product/manage/orders/list" + " " + constants.GET_URL_PARAMS,
  listAssistAdmin:
    "_product/manage/orders/assist-admin/list" + " " + constants.GET_URL_PARAMS,
  status:
    "_product/manage/orders/status" + " " + constants.PATCH_FORM_ID_URL_ENCODED,
  variant: "_product/manage/orders/count" + " " + constants.GET_URL_ID_PARAMS,
  variantWithoutBusinessId:
    "_product/manage/orders/count/variant-order" +
    " " +
    constants.GET_URL_ID_PARAMS,
  invoiceDownload:
    "_product/reports/orders/invoice" + " " + constants.GET_ID_PARAMS,
  markAsDelivered:
    "_product/manage/orders/mark-as-completed" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  checkStock: "_product/manage/orders/check-stock" + " " + constants.POST_RAW,
  checkStatus:
    "_product/manage/orders/check-status" + " " + constants.GET_ID_PARAMS,
  pendingDeliveryOrder:
    "_product/manage/orders/send-delivery-message" + " " + constants.POST_RAW,
};
export const orderReturn = {
  inform:
    "_product/manage/orders/product-return" + " " + constants.POST_ID_PARAMS,
  list:
    "_product/manage/orders/product-return" + " " + constants.GET_URL_ID_PARAMS,
  stockAdjust:
    "_product/manage/orders/product-return" + " " + constants.PATCH_ID,
  inventoryAdjust:
    "_product/manage/orders/product-inventory-adjust" +
    " " +
    constants.POST_ID_PARAMS,
  markInventoryAdjust:
    "_product/manage/orders/product-inventory-adjust" +
    " " +
    constants.PATCH_ID,
};
export const goodsLoadingArea = {
  add: "business/goods-loading-areas" + " " + constants.POST_RAW,
  list: "business/goods-loading-areas" + " " + constants.GET_URL_PARAMS,
  info: "business/goods-loading-areas" + " " + constants.GET_ID_PARAMS,
  update: "business/goods-loading-areas" + " " + constants.PATCH_ID,
  delete: "business/goods-loading-areas" + " " + constants.DELETE_ID_PARAMS,
};

export const routePlanning = {
  planSummary: "_product/v2/manage/route-plans/summary" + " " + constants.POST_RAW,
  availableVehicles: "_product/manage/route-plans/available-vehicles" + " " + constants.GET,
  updatePlanDate: "_product/manage/route-plans" + " " + constants.PATCH_ID,
  updatePlanLoadingManager: "_product/manage/route-plans/loading-manager" + " " + constants.PATCH_ID,
  updatePlanVehicleDriver: "_product/manage/route-plans/driver" + " " + constants.PATCH_ID,
  planCreate: "_product/v2/manage/route-plans/create" + " " + constants.POST_RAW,
  acknowledgeLastOrderDelivered: "_product/manage/route-plans/acknowledge-last-order-delivered" + " " + constants.POST_RAW,
  getPlan: "_product/manage/route-plans/list-by-vehicle" + " " + constants.GET_URL_PARAMS,
  planInfo: "_product/manage/route-plans/info" + " " + constants.GET_ID_PARAMS,
  driverAcknowledgement: "_product/manage/route-plans/driver-acknowledgement" + " " + constants.GET_ID_PARAMS,
  scheduleNextDay: "_product/manage/route-plans/schedule-next-day" + " " + constants.POST_RAW,
  vehicleInfo: "_product/manage/route-plans/vehicle/info" + " " + constants.GET_URL_ID_PARAMS,
  vehicleInfoBatchAssign: "_product/manage/route-plans/vehicle/assign-batch" + " " + constants.GET_URL_ID_PARAMS,
  vehicleMarkBatchAssigned: "_product/manage/route-plans/vehicle/mark-batch-assigned" + " " + constants.GET_URL_ID_PARAMS,
  vehicleOrderVariantStatus: "_product/manage/route-plans/vehicle/order/variant/status" + " " + constants.PATCH_FORM_ID_URL_ENCODED,
  vehicleRouteStatus: "_product/manage/route-plans/vehicle/status" + " " + constants.PATCH_FORM_ID_URL_ENCODED,
  vehicleMarkAsLoaded: "_product/manage/route-plans/vehicle/status/mark-loaded" + " " + constants.MULTI_PART_ID_PATCH,
  goodsReceivedInvoice: "_product/manage/route-plans/goods-received-invoices" + " " + constants.GET_URL_ID_PARAMS,
  ordersInvoice: "_product/reports/orders/invoices" + " " + constants.GET_URL_ID_PARAMS,
  orderInvoiceForThermalPrinter: "_product/reports/orders/invoices/thermal-printer-format" + " " + constants.GET_URL_ID_PARAMS,
  printCustomerCopy: "_product/reports/orders/print-customer-copy" + " " + constants.POST_RAW,
  vehicleOrderStatus: "_product/manage/route-plans/vehicle/order/status" + " " + constants.MULTI_PART_ID_PATCH,
  vehicleState: "_product/manage/route-plans/vehicle/state" + " " + constants.PATCH_ID,
  vehicleOrderSequence: "_product/manage/route-plans/vehicle/order" + " " + constants.PATCH_ID,
  vehicleOrderSubmitPartialPayment: "_product/manage/route-plans/vehicle/order/payment" + " " + constants.PATCH_ID,
  completeReview: "_product/manage/route-plans/complete-review" + " " + constants.PATCH_ID,
  addOrder: "_product/manage/route-plans/vehicle/order/add" + " " + constants.PATCH_ID,
  addPartialOrder: "_product/manage/route-plans/vehicle/order/add/partial" + " " + constants.PATCH_ID,
  removeOrder: "_product/manage/route-plans/vehicle/order/remove" + " " + constants.PATCH_ID,
  updateOrder: "_product/manage/route-plans/vehicle/order/update" + " " + constants.PATCH_ID,
  listOutForDeliveryPlans: "_product/manage/route-plans/list-out-for-delivery-plans" + " " + constants.GET_URL_PARAMS,
  getLastRoutePlanInfo: "_product/manage/route-plans/order-inventory-payment-transactions" + " " + constants.GET_URL_ID_PARAMS,
};

export const reports = {
  sellerPayout: "_product/reports/business-payouts" + " " + constants.POST_RAW,
  sellerReport:
    "_product/reports/orders-businesses" + " " + constants.GET_URL_PARAMS,
  outOfStockReport:
    "_product/metabase-reports/out-of-stock" + " " + constants.GET_URL_PARAMS,
  inventoryReplenishmentReport: "_product/reports/inventory-replenishment" + " " + constants.GET_URL_PARAMS,
  inventoryReplenishmentSync: "_product/reports/inventory-replenishment/sync" + " " + constants.POST_RAW,
  inventoryReplenishmentSyncStatus: "_product/reports/inventory-replenishment/sync" + " " + constants.GET_URL_PARAMS,
  inventoryReplenishmentSyncCancel: "_product/reports/inventory-replenishment/sync/cancel" + " " + constants.POST_RAW,
  customerReport:
    "_product/reports/orders-customers" + " " + constants.GET_URL_PARAMS,
  productsReport:
    "_product/reports/business-products" + " " + constants.GET_URL_PARAMS,
  productsPriceListReport:
    "_product/reports/business-products-price-list" +
    " " +
    constants.GET_URL_PARAMS,
  productVariantReport:
    "_product/reports/products-variants" + " " + constants.GET_URL_PARAMS,
  dashboardReport: "_product/reports/sales" + " " + constants.GET_URL_PARAMS,
  inventoryReport:
    "_product/reports/inventory" + " " + constants.GET_URL_PARAMS,
  customerRegistrationReport:
    "_product/reports/buyer-registration" + " " + constants.GET_URL_PARAMS,
  purchaseBehaviourReport:
    "_product/analytics/purchase-behaviour" + " " + constants.GET_URL_PARAMS,
  geographicSalesReport:
    "_product/analytics/geographic-sales-insight" +
    " " +
    constants.GET_URL_PARAMS,
  areaBasedCustomerReport:
    "_product/analytics/area-based-customer-segmentation" +
    " " +
    constants.GET_URL_PARAMS,
  frequentCustomerReport:
    "_product/analytics/frequent-customer-purchase-pattern-analysis" +
    " " +
    constants.GET_URL_PARAMS,
  productSalesPerformaceReport:
    "_product/analytics/product-sales-performance" +
    " " +
    constants.GET_URL_PARAMS,
  brandWiseSalesReport:
    "_product/analytics/brand-wise-sales-performance" +
    " " +
    constants.GET_URL_PARAMS,
  categorySubCategoryReport:
    "_product/analytics/category-sub-category-sales-analysis" +
    " " +
    constants.GET_URL_PARAMS,
  revenueGenerationReport:
    "_product/analytics/revenue-generation" + " " + constants.GET_URL_PARAMS,
  commissionAnalysisReport:
    "_product/analytics/commission-generation" + " " + constants.GET_URL_PARAMS,
  productVariantInventoryReport:
    "_product/analytics/product-variant-inventory-status" +
    " " +
    constants.GET_URL_PARAMS,
};
export const dashboard = {
  salesReport:
    "_product/manage/orders/sales-report2" + " " + constants.GET_URL_PARAMS,
  userReport:
    "_product/manage/orders/users-report" + " " + constants.GET_URL_PARAMS,
  orderReport:
    "_product/manage/orders/orders-report" + " " + constants.GET_URL_PARAMS,
  sellerReport:
    "_product/manage/orders/businesses-report" + " " + constants.GET_URL_PARAMS,
};
export const salesDashboard = {
  weeklySalesTrendsReport:
    "_product/manage/orders/assist-admin/weekly-sales-trend" +
    " " +
    constants.GET_URL_PARAMS,
  districtOrderTrendReport:
    "_product/manage/orders/assist-admin/district-orders-trend" +
    " " +
    constants.GET_URL_PARAMS,
};
export const preOrders = {
  list: "_product/pre-orders" + " " + constants.GET_URL_PARAMS,
};
export const appSettings = {
  privacyPolicy: "_default/privacy-policy" + " " + constants.GET,
  termsCondition: "_default/terms-conditions" + " " + constants.GET,
  appSettings: "config/app-settings" + " " + constants.GET_URL_PARAMS,
  addPrivacyPOlicy: "_default/privacy-policy" + " " + constants.POST_RAW,
  addTermsCondition: "_default/terms-conditions" + " " + constants.POST_RAW,
  addAppSettings: "config/app-settings" + " " + constants.PATCH,
};

/**
 * @description Platform settings API endpoints
 * - getSettings: @link utrade-backend/src/_platforms/_admin/controllers/v2/SettingController.ts#getSettings
 * - getConfigurationSetting: @link utrade-backend/src/_platforms/_admin/controllers/v2/SettingController.ts#getConfigurationSetting
 * - executeConfigurationAction: @link utrade-backend/src/_platforms/_admin/controllers/SettingController.ts#executeConfigurationAction
 * - updateConfigurationSetting: @link utrade-backend/src/_platforms/_admin/controllers/v2/SettingController.ts#updateConfigurationSetting
 */
/** Service requests (RTDK-491). Request actions take path `{ id, step }`, e.g. `.../:id/approve`. */
export const serviceRequest = {
  access: "service-request/access" + " " + constants.GET,
  listRequests: "service-request" + " " + constants.GET_URL_PARAMS,
  counts: "service-request/counts" + " " + constants.GET_URL_PARAMS,
  raiseRequest: "service-request" + " " + constants.POST_RAW,
  requestStep: "service-request" + " " + constants.PATCH_ID,
};

export const platformSettings = {
  getSettings: "v2/settings" + " " + constants.GET,
  getConfigurationSetting: "v2/settings/get" + " " + constants.GET_ID_PARAMS,
  executeConfigurationAction: "v2/settings/action" + " " + constants.POST_RAW,
  updateConfigurationSetting: "v2/settings/update" + " " + constants.PATCH,
};

export const interTransferRequest = {
  transferRequest:
    "_product/manage/stock-transfer-request" + " " + constants.POST_RAW,
  listTransferRequest:
    "_product/manage/stock-transfer-request/list" +
    " " +
    constants.GET_URL_PARAMS,
  tranferDetail:
    "_product/manage/stock-transfer-request/info" +
    " " +
    constants.GET_ID_PARAMS,
  buisnessPayout:
    "_product/manage/stock-transfer-request/payouts/business-orders" +
    " " +
    constants.GET_URL_ID_PARAMS,
  addPayout:
    "_product/manage/stock-transfer-request/payouts/business" +
    " " +
    constants.POST_ID_PARAMS,

  changeStatus:
    "_product/manage/stock-transfer-request/status" + " " + constants.PATCH_ID,
  infoInventory:
    "_product/manage/stock-transfer-request/completion-init" +
    " " +
    constants.GET_ID_PARAMS,
};
export const preOrderCustomer = {
  listBuyers: "_product/pre-orders/buyers" + " " + constants.GET_URL_PARAMS,
  listProducts: "_product/pre-orders/products" + " " + constants.GET_URL_PARAMS,
  listBusiness: "_product/pre-orders/business" + " " + constants.GET_URL_PARAMS,
};
export const customerCategory = {
  addCustomerCategory: "customer/categories" + " " + constants.POST_RAW,
  listCustomerCategory: "customer/categories" + " " + constants.GET_URL_PARAMS,
  editCustomerCategory: "customer/categories" + " " + constants.PATCH_ID,
  deleteCustomerCategory:
    "customer/categories" + " " + constants.DELETE_ID_PARAMS,
};
export const multipleWarehouse = {
  listWarehouse: "_product/warehouse" + " " + constants.GET_URL_PARAMS,
  warehouseInfo: "_product/warehouse" + " " + constants.GET_ID_PARAMS,
  addWarehouse: "_product/warehouse" + " " + constants.POST_RAW,
  updateWarehouse: "_product/warehouse" + " " + constants.PATCH_ID,
  warehouseStatus: "_product/warehouse/status" + " " + constants.PATCH_ID,
};
export const priceUpdateRequest = {
  list:
    "_product/manage/price-update-request/list" +
    " " +
    constants.GET_URL_PARAMS,
  info:
    "_product/manage/price-update-request/info" + " " + constants.GET_ID_PARAMS,
  approve:
    "_product/manage/price-update-request/approve" +
    " " +
    constants.POST_ID_PARAMS,
  reject:
    "_product/manage/price-update-request/reject" +
    " " +
    constants.POST_ID_PARAMS,
  changeStatus:
    "_product/manage/price-update-request/status" + " " + constants.PATCH_ID,
};
export const inventoryReturnRequest = {
  list: "_product/manage-return-products/list" + " " + constants.GET_URL_PARAMS,
  info:
    "_product/manage-return-products/details" + " " + constants.GET_ID_PARAMS,
  updateStatus:
    "_product/manage-return-products/status" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
};
export const purchaseOrder = {
  list: "" + " " + constants.GET_URL_PARAMS,
};
export const vendor = {
  listVendor: "vendor/manage-business/list" + " " + constants.GET_URL_PARAMS,
  addVendor: "vendor/manage-business/add" + " " + constants.POST_FORM,
  vendorProfile: "business/profile" + " " + constants.GET_ID_PARAMS,
  kycStatusUpdate:
    "vendor/manage-business/kyc/status" +
    " " +
    constants.PATCH_FORM_ID_URL_ENCODED,
  vendorProfileUpdate:
    "vendor/manage-business/profile" + " " + constants.MULTI_PART_ID_PATCH,
  listProduct: "_product/vendor-products/list" + " " + constants.GET_URL_PARAMS,

  listVariant:
    "_product/vendor-products/list-variants" + " " + constants.GET_URL_PARAMS,
  assignProductToVendor:
    "_product/vendor-products/assign-product-to-vendor" +
    " " +
    constants.POST_RAW,
  unassignProductToVendor:
    "_product/vendor-products/unassign-vendor-from-product" +
    " " +
    constants.POST_RAW,
  payoutProfiles: "business/payout-profiles" + " " + constants.GET_URL_PARAMS,
  payoutOrderList:
    "_product/manage/shipments/vendors/payouts/business-orders" +
    " " +
    constants.GET_URL_ID_PARAMS,
  payoutInfo:
    "_product/manage/shipments/vendors/payouts" + " " + constants.GET_ID_PARAMS,
  payoutList:
    "_product/manage/shipments/vendors/payouts/business" +
    " " +
    constants.GET_URL_ID_PARAMS,
  payout:
    "_product/manage/shipments/vendors/payouts/business" +
    " " +
    constants.MULTI_PART_ID_POST,
  bankDetails:
    "vendor/manage-business/bank-details" + " " + constants.GET_ID_PARAMS,
};
export const DEFAULT = {
  CERTIFICATES:
    "vendor/default/business/certificate" + " " + constants.GET_URL_PARAMS,
  BUSINESSTYPES:
    "vendor/default/business/types" + " " + constants.GET_URL_PARAMS,
  DISTRICTS: "_default/districts" + " " + constants.GET_URL_PARAMS,
};

export const PURCHASEORDER = {
  draftPurchaseOrders: "_product/reports/draft-purchase-orders" + " " + constants.GET_URL_PARAMS,
  draftPurchaseOrderDetails: "_product/reports/draft-purchase-orders" + " " + constants.GET_ID_PARAMS,
  deleteDraftPurchaseOrder: "_product/reports/draft-purchase-orders" + " " + constants.PATCH_ID,
  updateDraftPurchaseOrder: "_product/reports/draft-purchase-orders" + " " + constants.PATCH_ID,
  createDraftPurchaseOrder: "_product/reports/draft-purchase-orders" + " " + constants.POST_RAW,
  poSettings: "_product/reports/po-settings" + " " + constants.GET_URL_PARAMS,
  purchaseOrderTimeline: "_product/reports/purchase-orders" + " " + constants.GET_URL_ID_PARAMS,
  vendorCatalog: "_product/reports/vendor-catalog" + " " + constants.GET_URL_ID_PARAMS,
  updateVendorCatalog: "_product/reports/vendor-catalog" + " " + constants.PATCH_ID,
  // supplier groups (Draft PO groups); path { business } or { business, group } (+ "status" / "banks")
  supplierGroups: "_product/reports/supplier-groups" + " " + constants.GET_URL_ID_PARAMS,
  addSupplierGroup: "_product/reports/supplier-groups" + " " + constants.POST_ID_PARAMS,
  updateSupplierGroup: "_product/reports/supplier-groups" + " " + constants.PATCH_ID,
  productSuppliers: "_product/reports/product-suppliers" + " " + constants.GET_URL_ID_PARAMS,
  procurementParamsTemplate: "_product/reports/procurement-params/template" + " " + constants.GET,
  procurementParamsCheck: "_product/reports/procurement-params/check" + " " + constants.MULTI_PART_POST,
  procurementParamsApply: "_product/reports/procurement-params/apply" + " " + constants.MULTI_PART_POST,
  // Settings > Data fixes: customer TIN / phone ({ kind: "tin" | "phone" }); check, run, log of changes
  customerDataFixCheck: "_product/reports/customer-data-fix" + " " + constants.GET_URL_PARAMS,
  customerDataFixRun: "_product/reports/customer-data-fix" + " " + constants.POST_RAW,
  customerDataFixLog: "_product/reports/customer-data-fix/log" + " " + constants.GET_URL_PARAMS,
  listPurchaseOrders:
    "_product/manage/shipments/vendors/list" + " " + constants.GET_URL_PARAMS,
  lowStock: "_product/manage/low-stock" + " " + constants.GET_URL_ID_PARAMS,
  updateDeliveryDate:
    "_product/manage/shipments/delivery-date" + " " + constants.PATCH_ID,
  purchaseOrderInfo:
    "_product/manage/shipments/vendors" + " " + constants.GET_ID_PARAMS,
  productListForPurchaseOrder:
    "_product/vendor-products/list-variants" + " " + constants.GET_URL_PARAMS,
  productPrice:
    "_product/manage/shipments/vendors/product-price" +
    " " +
    constants.GET_URL_ID_PARAMS,
  addPurchaseOrder:
    "_product/manage/shipments/vendors" + " " + constants.POST_ID_PARAMS,
  updatePurchaseOrderStatus:
    "_product/manage/shipments/vendors/status" +
    " " +
    constants.PATCH_FORM_ID_V2,
  infoInventory:
    "_product/manage/shipments/vendors/completion-init" +
    " " +
    constants.GET_ID_PARAMS,
  updateSellingPrice:
    "_product/manage/shipments/vendors/price" + " " + constants.PATCH_ID,
  poDownload:
    "_product/manage/shipments/vendors/invoice" + " " + constants.GET_ID_PARAMS,
  grnDownload:
    "_product/manage/shipments/vendors/grn-invoice" +
    " " +
    constants.GET_URL_ID_PARAMS,
  allGrnDownload:
    "_product/manage/shipments/vendors/grn-invoice/all" +
    " " +
    constants.GET_URL_ID_PARAMS,
  downloadSample: "static_files/po_sample.xlsx" + " " + constants.GET,
  bulkAdd: "_product/manage/shipments/bulk-add" + " " + constants.POST_RAW,
  bulkAddValidations:
    "_product/manage/shipments/bulk-add-validations" +
    " " +
    constants.POST_FORM,
};

export const ESCALATIONS = {
  addEscalation: "_product/escalations" + " " + constants.POST_FORM,
  listEscalation: "_product/escalations" + " " + constants.GET_URL_PARAMS,
  escalationInfo: "_product/escalations" + " " + constants.GET_ID_PARAMS,
  updateStatus: "_product/escalations/status" + " " + constants.PATCH_ID,
};

/** UProcure Inward (guarded server-side by the Inward permissions) */
export const INWARD = {
  listPurchaseOrders:
    "_product/inward/purchase-orders" + " " + constants.GET_URL_PARAMS,
  purchaseOrderForGrn:
    "_product/inward/purchase-orders" + " " + constants.GET_ID_PARAMS,
  /** path: { id: poId, action: "grn" } -> _product/inward/purchase-orders/:id/grn */
  createGrn:
    "_product/inward/purchase-orders" + " " + constants.POST_ID_PARAMS,
  /** path: { id: poId, action: "start" } -> records "Inward started" */
  startInward:
    "_product/inward/purchase-orders" + " " + constants.POST_ID_PARAMS,
  /** path: { id: poId, action: "assignment" }, body: { loadingBayManager, dock } */
  assignLoadingBay:
    "_product/inward/purchase-orders" + " " + constants.POST_ID_PARAMS,
  /** path: { id: poId, action: "assignment-options" } */
  assignmentOptions:
    "_product/inward/purchase-orders" + " " + constants.GET_URL_ID_PARAMS,
  filterOptions:
    "_product/inward/filter-options" + " " + constants.GET_URL_PARAMS,
  listGrns: "_product/inward/grns" + " " + constants.GET_URL_PARAMS,
  grnInfo: "_product/inward/grns" + " " + constants.GET_ID_PARAMS,
  /** path: { id, action: "warehouse-approval" | "finance-approval" }, body: FormData */
  approveGrn: "_product/inward/grns" + " " + constants.MULTI_PART_ID_POST,
  /** path: { id, action: "retry-stock" } */
  retryStock: "_product/inward/grns" + " " + constants.POST_ID_PARAMS,
  /** path: { id, a: "cost-changes", alertId, b: "acknowledge" } */
  acknowledgeCost: "_product/inward/grns" + " " + constants.POST_ID_PARAMS,
  /** path: { id, a: "efd-verification" }, body: { verified: boolean } (Inward right 76) */
  setEfdVerification: "_product/inward/grns" + " " + constants.PATCH_ID,
  /** query: { date: YYYY-MM-DD } */
  dailyReport: "_product/inward/report" + " " + constants.GET_URL_PARAMS,
  /** same query; response is the .xlsx file (call with responseType "blob") */
  dailyReportExcel:
    "_product/inward/report/excel" + " " + constants.GET_URL_PARAMS,
  /** path: { id, a: "selling-price" }, body: { variant, type, sellingPrice, note } */
  updateSellingPrice: "_product/inward/grns" + " " + constants.POST_ID_PARAMS,
  /** path: { id, a: "reject" }, body: { reason } (Warehouse or Finance) */
  rejectGrn: "_product/inward/grns" + " " + constants.POST_ID_PARAMS,
  /** path: { id, a: "shortfall" }, body: { variant, type, decision: "approve" | "reject", note } */
  decideShortfall: "_product/inward/grns" + " " + constants.POST_ID_PARAMS,
  /** pending actions for the logged-in user (all for a super admin), with SLA state */
  pendingActions: "_product/inward/pending-actions" + " " + constants.GET_URL_PARAMS,
  settings: "_product/inward/settings" + " " + constants.GET_URL_PARAMS,
  /** body: { sla: { startInward, warehouseApproval, financeApproval, costAcknowledgement } } */
  updateSettings: "_product/inward/settings" + " " + constants.PATCH,
};

export const poManagementEndPoints = {
  genarateGrn:
    "_product/manage/shipments/vendors/status" +
    " " +
    constants.PATCH_FORM_ID_V2,
};

export const assistAdmin = {
  list: "assist-admin" + " " + constants.GET_URL_PARAMS,
  add: "assist-admin" + " " + constants.POST_FORM,
  info: "assist-admin" + " " + constants.GET_URL_ID_PARAMS,
  assignedCustomers:
    "_buyer/manage-buyers/list-buyers-sales-manager" +
    " " +
    constants.GET_URL_PARAMS,
  checkAssignedDistrict:
    "assist-admin/check-assigned" + " " + constants.POST_RAW,
  removeAssignedDistrict:
    "assist-admin/remove-assigned" + " " + constants.PATCH,
  edit: "assist-admin" + " " + constants.PATCH_ID,
  status: "assist-admin/status" + " " + constants.PATCH_ID,
};

export const targetManagement = {
  list: "assist-admin" + " " + constants.GET_URL_PARAMS,
  addEditTarget: "assist-admin/assign-targets" + " " + constants.POST_ID_PARAMS,
};

export const customerAssignment = {
  list:
    "_buyer/manage-buyers/list-buyers-sales-manager" +
    " " +
    constants.GET_URL_PARAMS,
  assign: "_buyer/manage-buyers/assign-member" + " " + constants.POST_RAW,
};

export const operations = {
  branchHealth: "operations/branches/health" + " " + constants.GET_URL_PARAMS,
};
